/**
 * Prompt template helpers for the Model Configuration prompt editor
 * (Figma Model Configuration, .ModelSetup-PromptEditor / .ModelSetup-PromptToken).
 *
 * A template mixes plain text with `{{token}}` placeholders:
 * - `{{example}}` is the primary model input (dataset example) — rendered as
 *   the teal "input" pill.
 * - Any other `{{name}}` is an extra dataset field used as context — rendered
 *   as a blue token pill.
 */

export const EXAMPLE_TOKEN = 'example';

const TOKEN_SPLIT_RE = /(\{\{[^}]+\}\})/g;
const TOKEN_MATCH_RE = /^\{\{([^}]+)\}\}$/;

/**
 * Split a template into ordered segments:
 * `{ type: 'text', value }` | `{ type: 'token', name, raw }`.
 */
export const tokenizePromptTemplate = (template) => {
    if (!template) return [];
    return template
        .split(TOKEN_SPLIT_RE)
        .filter((part) => part !== '')
        .map((part) => {
            const match = part.match(TOKEN_MATCH_RE);
            if (match) {
                return { type: 'token', name: match[1].trim(), raw: part };
            }
            return { type: 'text', value: part };
        });
};

/** Number of `{{name}}` occurrences in the template. */
export const countTokenUsage = (template, name) => {
    if (!template) return 0;
    return tokenizePromptTemplate(template).filter(
        (segment) => segment.type === 'token' && segment.name === name
    ).length;
};

/** Field names (excluding `example`) used as `{{name}}` in the template. */
export const extraFieldNamesInTemplate = (template) =>
    [...new Set(
        tokenizePromptTemplate(template)
            .filter((segment) => segment.type === 'token' && segment.name !== EXAMPLE_TOKEN)
            .map((segment) => segment.name)
    )];

/**
 * Append `{{name}}` to the template on its own line (Additional fields
 * "Insert" action).
 */
export const insertTokenIntoTemplate = (template, name) => {
    const token = `{{${name}}}`;
    const base = template || '';
    if (!base.trim()) return token;
    return base.endsWith('\n') ? `${base}${token}` : `${base}\n${token}`;
};

const toDisplayValue = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

/**
 * Preview segments: tokens resolved to their sample values.
 * `{ type: 'text', value }` | `{ type: 'value', name, value, missing }`.
 * `example` resolves from `exampleValue`; other tokens from `paramValues`.
 */
export const resolvePromptPreviewSegments = (template, { exampleValue, paramValues } = {}) =>
    tokenizePromptTemplate(template).map((segment) => {
        if (segment.type === 'text') return segment;
        const raw = segment.name === EXAMPLE_TOKEN
            ? exampleValue
            : paramValues?.[segment.name];
        const value = toDisplayValue(raw);
        return {
            type: 'value',
            name: segment.name,
            value,
            missing: !value,
        };
    });
